/**
 * THE JEST SDK, BEHIND ONE DOOR. Phase 26, DESIGN-DELTAS 1ag. Reference `docs/JEST.md` 1.3.
 *
 * Jest.com hosts the game on its CDN and hands it a player, a payment counter, a text-message
 * scheduler and a share sheet through a global the page loads from `cdn.jest.com`. Every call
 * the game makes to that global goes through this file, for three reasons:
 *
 *   1. THE SDK MAY BE ABSENT. The ordinary build at ashport.city never loads it, and a Jest build
 *      opened outside their platform (the hosted emulator half-configured, a developer's tab) has
 *      the tag and no global. Every function here answers null when there is nothing to call, so
 *      the ordinary build can import this module without cost and the Jest build never throws
 *      on a missing platform, it says so.
 *   2. TWO NAMES WERE SEEN ONLY IN THEIR TUTORIAL. `markFirstMilestone` and `captureEvent` are
 *      not in the SDK reference pages read on 2026-09-07. Each is feature-detected before it is
 *      called, and so is everything else, because a platform SDK is theirs to rename.
 *   3. NOTHING HERE IS GAME STATE. The server is the record; `JestSDK.data` is not used. What
 *      this file keeps is two preferences in `localStorage` (the reminder switch, the milestone
 *      flag), both per browser and both harmless to lose.
 */

import { api, setBearer } from './net/api'
import type { JestLoginResult } from '@ashport/shared'

/* ------------------------------------------------------------------------- the SDK's shapes */

export interface JestPlayer {
  playerId: string
  registered: boolean
  username: string | null
  avatarUrl: string | null
}

/** `PurchaseData`, as their payments page describes it. The token is the purchase's identity. */
export interface JestPurchaseData {
  purchaseToken: string
  productSku: string
  createdAt?: string
  completedAt?: string | null
  estimatedRevenue?: number
  price?: number
  currency?: string
  sandbox?: boolean
}

export type JestBeginResult =
  | { result: 'success'; purchase: JestPurchaseData; purchaseSigned: string }
  | { result: 'cancel' }
  | { result: 'error'; error: string }

export type JestCompleteResult = { result: 'success' } | { result: 'error'; error: string }

export interface JestIncomplete {
  hasMore: boolean
  purchases: JestPurchaseData[]
  purchasesSigned: string
}

export interface JestNotification {
  scheduledInDays?: number
  scheduledAt?: Date
  priority: 'low' | 'medium' | 'high' | 'critical'
  body: string
  title?: string
  ctaText?: string
  identifier?: string
  assetReference?: string
  entryPayload?: Record<string, unknown>
}

/**
 * The global, every member optional, because a feature-detected call is the only kind this
 * file makes. `docs/JEST.md` 1.3 is the reference this was typed from.
 */
export interface JestSdk {
  init?: (o?: { autoLoginReminders?: boolean }) => Promise<void>
  setLoadingProgress?: (n: number) => void
  markGameLoaded?: () => void
  getPlayer?: () => JestPlayer
  getPlayerSigned?: () => Promise<{ player: JestPlayer; playerSigned: string }>
  login?: (o?: { entryPayload?: Record<string, unknown> }) => Promise<void>
  getEntryPayload?: () => Record<string, unknown>
  markFirstMilestone?: () => void
  captureEvent?: (name: string, props?: Record<string, unknown>) => void
  lifecycle?: {
    onHide?: (fn: () => void) => () => void
    onShow?: (fn: () => void) => () => void
    onExitRequested?: (fn: () => Promise<void> | void) => () => void
  }
  notifications?: {
    scheduleNotification?: (o: JestNotification) => unknown
    unscheduleNotification?: (o: { identifier: string }) => unknown
  }
  referrals?: {
    shareReferralLink?: (o: { reference: string; entryPayload?: Record<string, unknown> }) => Promise<{ canceled: boolean }>
  }
  payments?: {
    getProducts?: () => Promise<{ sku: string; name: string; description: string; price: number; currency: string }[]>
    beginPurchase?: (o: { productSku: string }) => Promise<JestBeginResult>
    completePurchase?: (o: { purchaseToken: string }) => Promise<JestCompleteResult>
    getIncompletePurchases?: () => Promise<JestIncomplete>
  }
}

declare global {
  interface Window {
    JestSDK?: JestSdk
  }
}

/* ------------------------------------------------------------------------------ the door */

/** The global, or null. Never cached: the tag may load after the first script runs. */
export function sdk(): JestSdk | null {
  if (typeof window === 'undefined') return null
  const s = window.JestSDK
  return s && typeof s === 'object' ? s : null
}

const fn = <T>(x: T | undefined): x is T => typeof x === 'function'

/** Nothing may be called before this. Resolves false when there is no SDK to initialise. */
export async function jestInit(): Promise<boolean> {
  const s = sdk()
  if (!s || !fn(s.init)) return false
  await s.init()
  return true
}

/**
 * The loading overlay is the platform's, and it exits the player to their home screen if no
 * progress arrives within fifteen seconds. `JestGate` reports 30 before `init`; `App` reports
 * 100 once the boot read has answered.
 */
export function jestProgress(n: number): void {
  const s = sdk()
  if (s && fn(s.setLoadingProgress)) s.setLoadingProgress(Math.max(0, Math.min(100, Math.round(n))))
}

export function jestLoaded(): void {
  const s = sdk()
  if (s && fn(s.markGameLoaded)) s.markGameLoaded()
  else jestProgress(100)
}

/** The player as the platform sees them now, synchronous. `registered` is the word that matters. */
export function jestPlayerNow(): JestPlayer | null {
  const s = sdk()
  if (!s || !fn(s.getPlayer)) return null
  try {
    return s.getPlayer() ?? null
  } catch {
    return null
  }
}

export async function jestPlayerSigned(): Promise<{ player: JestPlayer; playerSigned: string } | null> {
  const s = sdk()
  if (!s || !fn(s.getPlayerSigned)) return null
  const r = await s.getPlayerSigned()
  return r && typeof r.playerSigned === 'string' && r.playerSigned !== '' ? r : null
}

/**
 * The whole sign-in, in one place, because two screens do it: the `JestGate` on every open, and
 * the reminders card after a guest registers. The signed player goes to the server, the bearer
 * the server answers with goes to `api.ts`, and the caller boots through the store as any build
 * does. Null when the SDK is absent or handed back no signature; a server refusal throws in
 * voice as an `ApiError`.
 */
export async function jestSignIn(): Promise<JestLoginResult | null> {
  const signed = await jestPlayerSigned()
  if (!signed) return null
  const r = await api.jestLogin(signed.playerSigned)
  setBearer(r.token)
  return r
}

/** Register a guest: the platform's popup. Resolves when it is dismissed. False without an SDK. */
export async function jestLogin(entryPayload?: Record<string, unknown>): Promise<boolean> {
  const s = sdk()
  if (!s || !fn(s.login)) return false
  await s.login(entryPayload ? { entryPayload } : undefined)
  return true
}

/** Where a notification or a referral link said to land. `{}` when nowhere. */
export function jestEntryPayload(): Record<string, unknown> {
  const s = sdk()
  if (!s || !fn(s.getEntryPayload)) return {}
  try {
    const p = s.getEntryPayload()
    return p && typeof p === 'object' ? p : {}
  } catch {
    return {}
  }
}

/* --------------------------------------------------------------------------------- money */

export async function jestBeginPurchase(sku: string): Promise<JestBeginResult | null> {
  const s = sdk()
  if (!s?.payments || !fn(s.payments.beginPurchase)) return null
  return s.payments.beginPurchase({ productSku: sku })
}

export async function jestCompletePurchase(purchaseToken: string): Promise<JestCompleteResult | null> {
  const s = sdk()
  if (!s?.payments || !fn(s.payments.completePurchase)) return null
  return s.payments.completePurchase({ purchaseToken })
}

export async function jestIncompletePurchases(): Promise<JestIncomplete | null> {
  const s = sdk()
  if (!s?.payments || !fn(s.payments.getIncompletePurchases)) return null
  const r = await s.payments.getIncompletePurchases()
  return r && Array.isArray(r.purchases) ? r : null
}

/* ----------------------------------------------------------------------------- reminders */

/**
 * THREE TEXTS, TIED TO THE GAME'S OWN REASONS TO COME BACK. DESIGN-DELTAS 1ag.7.
 *
 * Their rules, from the tutorial: registered players only (the platform enforces it), at most
 * one text per player per day across every game (the platform chooses), short bodies, no emoji,
 * no game name because the platform frames the message. Each body is under 100 characters and
 * every one lands on Jobs, which is where Energy goes. `scheduledInDays` is 1 to 7.
 */
export const REMINDERS: { identifier: string; scheduledInDays: number; body: string }[] = [
  { identifier: 'back-1', scheduledInDays: 1, body: 'Your Energy is full and the Beat has moved. The street is waiting.' },
  { identifier: 'back-3', scheduledInDays: 3, body: 'Three nights gone. Somebody has been working your corner.' },
  { identifier: 'back-7', scheduledInDays: 7, body: 'A week away. Your Heat has cooled and your rank has not moved.' },
]

export const REMIND_KEY = 'ashport.jest.remind'
const MILESTONE_KEY = 'ashport.jest.milestone'

const readLocal = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

const writeLocal = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    /* private mode, or storage refused: the preference is per browser and harmless to lose */
  }
}

/** The switch on the reminders card. On until the player turns it off. */
export function jestRemindPref(): boolean {
  return readLocal(REMIND_KEY) !== '0'
}

export function setJestRemindPref(on: boolean): void {
  writeLocal(REMIND_KEY, on ? '1' : '0')
}

export function jestSchedule(n: JestNotification): boolean {
  const s = sdk()
  if (!s?.notifications || !fn(s.notifications.scheduleNotification)) return false
  s.notifications.scheduleNotification(n)
  return true
}

export function jestUnschedule(identifier: string): boolean {
  const s = sdk()
  if (!s?.notifications || !fn(s.notifications.unscheduleNotification)) return false
  s.notifications.unscheduleNotification({ identifier })
  return true
}

/**
 * Apply the switch: schedule the three when it is on and the player is registered, unschedule
 * the three when it is off. Called on every ready boot and on every flip of the switch, because
 * the platform holds the schedule and re-scheduling an identifier replaces it. Returns how many
 * calls reached the SDK, which is zero without one.
 */
export function jestReminders(on: boolean, registered: boolean): number {
  let n = 0
  if (on && registered) {
    for (const r of REMINDERS) {
      if (jestSchedule({ ...r, priority: 'medium', entryPayload: { route: 'jobs' } })) n += 1
    }
    return n
  }
  if (!on) {
    for (const r of REMINDERS) if (jestUnschedule(r.identifier)) n += 1
  }
  return n
}

/* ---------------------------------------------------------------------- referrals, signals */

export async function jestShareReferral(reference: string, entryPayload?: Record<string, unknown>): Promise<{ canceled: boolean } | null> {
  const s = sdk()
  if (!s?.referrals || !fn(s.referrals.shareReferralLink)) return null
  return s.referrals.shareReferralLink(entryPayload ? { reference, entryPayload } : { reference })
}

/**
 * Tutorial-only names, detected before they are called. Once per browser for the milestone: the
 * platform wants the first meaningful moment, not every level after it.
 */
export function jestFirstMilestone(): boolean {
  if (readLocal(MILESTONE_KEY) === '1') return false
  const s = sdk()
  if (!s || !fn(s.markFirstMilestone)) return false
  s.markFirstMilestone()
  writeLocal(MILESTONE_KEY, '1')
  return true
}

export function jestEvent(name: string, props?: Record<string, unknown>): boolean {
  const s = sdk()
  if (!s || !fn(s.captureEvent)) return false
  s.captureEvent(name, props)
  return true
}
